import React, { useState, useEffect } from 'react';

const CONFERENCE_API = process.env.REACT_APP_CONFERENCE_API_URL || 'http://localhost:8080/conference';
const REGISTRATION_API = process.env.REACT_APP_REGISTRATION_API_URL || 'http://localhost:8085';
const REVIEW_API = process.env.REACT_APP_REVIEW_API_URL || 'http://localhost:8082';

const PaperManagement = () => {
  const [user, setUser] = useState(null);
  const [conferences, setConferences] = useState([]);
  const [selectedConferenceId, setSelectedConferenceId] = useState('');
  const [tracks, setTracks] = useState([]);
  const [selectedTrackId, setSelectedTrackId] = useState('');
  const [papers, setPapers] = useState([]);
  const [allPapers, setAllPapers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('adminUser');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  useEffect(() => {
    fetchActiveConferences();
  }, []);

  const fetchActiveConferences = async () => {
    try {
      console.log('Fetching conferences from:', `${CONFERENCE_API}/active`);
      const response = await fetch(`${CONFERENCE_API}/active`);
      console.log('Conference response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched conferences:', data);
        setConferences(data);
      } else {
        console.error('Failed to fetch conferences:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching conferences:', error);
    }
  };

  const fetchTracks = async (conferenceId) => {
    try {
      const response = await fetch(`${CONFERENCE_API}/api/tracks/conference/${conferenceId}`);
      if (response.ok) {
        const data = await response.json();
        console.log('Tracks data:', data);
        setTracks(data);
      } else {
        console.error('Failed to fetch tracks:', response.status);
        setTracks([]);
      }
    } catch (error) {
      console.error('Error fetching tracks:', error);
      setTracks([]);
    }
  };

  const fetchPapersWithReviewers = async (conferenceId) => {
    if (!conferenceId) return;

    setLoading(true);
    try {
      // Fetch tracks for the conference
      await fetchTracks(conferenceId);
      
      // Fetch papers from registration service
      const selectedConf = conferences.find(c => c.id === parseInt(conferenceId));
      if (!selectedConf) {
        setPapers([]);
        setAllPapers([]);
        setLoading(false);
        return;
      }

      const papersResponse = await fetch(`${REGISTRATION_API}/api/papers/conference/${encodeURIComponent(selectedConf.name)}`);
      if (!papersResponse.ok) {
        setPapers([]);
        setAllPapers([]);
        setLoading(false);
        return;
      }

      const papersData = await papersResponse.json();
      
      // Fetch reviewer assignments for each paper
      const papersWithReviewers = await Promise.all(
        papersData.map(async (paper) => {
          try {
            const assignmentsResponse = await fetch(`${REVIEW_API}/review/review-assignments/paper/${paper.id}`);
            if (assignmentsResponse.ok) {
              const assignments = await assignmentsResponse.json();
              
              // Fetch reviewer names
              const reviewerNames = await Promise.all(
                assignments.map(async (assignment) => {
                  try {
                    const reviewerResponse = await fetch(`${CONFERENCE_API}/api/submission-reviewers/${assignment.reviewerId}`);
                    if (reviewerResponse.ok) {
                      const reviewer = await reviewerResponse.json();
                      return reviewer.fullName || reviewer.email;
                    }
                  } catch (err) {
                    console.error(`Error fetching reviewer ${assignment.reviewerId}:`, err);
                  }
                  return `Reviewer ${assignment.reviewerId}`;
                })
              );

              return {
                ...paper,
                assignedReviewers: assignments.length,
                reviewerNames: reviewerNames,
                // Use the actual paper status from registration service
                reviewStatus: paper.status
              };
            }
            return {
              ...paper,
              assignedReviewers: 0,
              reviewerNames: [],
              // Use the actual paper status from registration service
              reviewStatus: paper.status
            };
          } catch (error) {
            console.error(`Error fetching assignments for paper ${paper.id}:`, error);
            return {
              ...paper,
              assignedReviewers: 0,
              reviewerNames: [],
              // Use the actual paper status from registration service
              reviewStatus: paper.status
            };
          }
        })
      );

      setAllPapers(papersWithReviewers);
      setPapers(papersWithReviewers);
    } catch (error) {
      console.error('Error fetching papers:', error);
      setPapers([]);
      setAllPapers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedConferenceId) {
      setSelectedTrackId('');
      fetchPapersWithReviewers(selectedConferenceId);
    } else {
      setPapers([]);
      setAllPapers([]);
      setTracks([]);
      setSelectedTrackId('');
    }
  }, [selectedConferenceId]);

  useEffect(() => {
    // Filter papers by selected track
    if (selectedTrackId) {
      console.log('Filtering by track:', selectedTrackId, 'from', allPapers.length, 'papers');
      const filtered = allPapers.filter(paper => paper.trackId == selectedTrackId);
      console.log('Filtered papers:', filtered.length);
      setPapers(filtered);
    } else {
      console.log('Showing all papers:', allPapers.length);
      setPapers(allPapers);
    }
  }, [selectedTrackId, allPapers]);

  const showAlert = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  const getStatusColor = (status) => {
    const colors = {
      'PENDING_REVIEW': '#6c757d',
      'UNDER_REVIEW': '#ffc107',
      'REVIEWED': '#17a2b8',
      'ACCEPTED': '#28a745',
      'REJECTED': '#dc3545',
      'REVISION_REQUESTED': '#fd7e14'
    };
    return colors[status] || '#6c757d';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div style={{ padding: '20px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ margin: 0, color: '#333' }}>Paper Management Dashboard</h2>
        {user && user.role === 'CHAIR' && (
          <p style={{ color: '#666', marginTop: '8px', marginBottom: 0 }}>
            View all papers with assigned reviewers and status
          </p>
        )}
      </div>

      {alert && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: alert.type === 'success' ? '#d4edda' : '#f8d7da',
          color: alert.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${alert.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {alert.message}
        </div>
      )}

      <div style={{ marginBottom: '30px', backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', gap: '20px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
              Select Conference:
            </label>
            <select
              value={selectedConferenceId}
              onChange={(e) => setSelectedConferenceId(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value="">-- Select Conference --</option>
              {conferences.map(conf => (
                <option key={conf.id} value={conf.id}>
                  {conf.name}
                </option>
              ))}
            </select>
          </div>

          {selectedConferenceId && tracks.length > 0 && (
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
                Filter by Track:
              </label>
              <select
                value={selectedTrackId}
                onChange={(e) => setSelectedTrackId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="">-- All Tracks --</option>
                {tracks.map(track => (
                  <option key={track.id} value={track.id}>
                    {track.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          Loading papers...
        </div>
      )}

      {!loading && selectedConferenceId && papers.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#666',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ marginTop: 0, color: '#333' }}>No Papers Found</h3>
          <p>No papers have been submitted to this conference yet.</p>
        </div>
      )}

      {!loading && papers.length > 0 && (
        <>
          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
            <div style={{ 
              backgroundColor: 'white', 
              padding: '20px', 
              borderRadius: '8px', 
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              borderLeft: '4px solid #007bff'
            }}>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Total Papers</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#333' }}>{papers.length}</div>
            </div>
            <div style={{ 
              backgroundColor: 'white', 
              padding: '20px', 
              borderRadius: '8px', 
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              borderLeft: '4px solid #ffc107'
            }}>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Under Review</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#333' }}>
                {papers.filter(p => p.reviewStatus === 'UNDER_REVIEW').length}
              </div>
            </div>
            <div style={{ 
              backgroundColor: 'white', 
              padding: '20px', 
              borderRadius: '8px', 
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              borderLeft: '4px solid #6c757d'
            }}>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Pending Review</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#333' }}>
                {papers.filter(p => p.reviewStatus === 'PENDING_REVIEW' || !p.assignedReviewers || p.assignedReviewers === 0).length}
              </div>
            </div>
            <div style={{ 
              backgroundColor: 'white', 
              padding: '20px', 
              borderRadius: '8px', 
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              borderLeft: '4px solid #28a745'
            }}>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Reviewed</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#333' }}>
                {papers.filter(p => ['ACCEPTED', 'REJECTED', 'REVISION_REQUESTED'].includes(p.reviewStatus)).length}
              </div>
            </div>
            <div style={{ 
              backgroundColor: 'white', 
              padding: '20px', 
              borderRadius: '8px', 
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              borderLeft: '4px solid #17a2b8'
            }}>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Avg Reviewers/Paper</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#333' }}>
                {papers.length > 0 ? (papers.reduce((acc, p) => acc + p.assignedReviewers, 0) / papers.length).toFixed(1) : '0.0'}
              </div>
            </div>
          </div>

          {/* Papers Table */}
          <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#333' }}>ID</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#333' }}>Title</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#333' }}>Author</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#333' }}>Track</th>
                  <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', color: '#333' }}>Presentation Mode</th>
                  <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', color: '#333' }}>Submitted</th>
                  <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', color: '#333' }}>Status</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#333' }}>Assigned Reviewers</th>
                </tr>
              </thead>
              <tbody>
                {papers.map(paper => (
                  <tr key={paper.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                    <td style={{ padding: '16px', color: '#495057' }}>{paper.id}</td>
                    <td style={{ padding: '16px', color: '#212529', fontWeight: '500' }}>
                      {paper.title}
                    </td>
                    <td style={{ padding: '16px', color: '#495057' }}>{paper.authorName}</td>
                    <td style={{ padding: '16px', color: '#495057' }}>{paper.trackName || '-'}</td>
                    <td style={{ padding: '16px', textAlign: 'center', color: '#495057' }}>
                      {paper.presentationMode ? (
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '12px',
                          backgroundColor: '#e7f3ff',
                          color: '#0d47a1',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {paper.presentationMode}
                        </span>
                      ) : '-'}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center', color: '#495057' }}>
                      {formatDate(paper.submissionDate)}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <span style={{
                        padding: '6px 12px',
                        borderRadius: '16px',
                        backgroundColor: getStatusColor(paper.reviewStatus),
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {paper.reviewStatus.replace('_', ' ')}
                      </span>
                    </td>
                    <td style={{ padding: '16px' }}>
                      {paper.reviewerNames && paper.reviewerNames.length > 0 ? (
                        <div style={{ fontSize: '14px', color: '#495057' }}>
                          {paper.reviewerNames.join(', ')}
                        </div>
                      ) : (
                        <span style={{ fontSize: '14px', color: '#999' }}>-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!selectedConferenceId && (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px', 
          color: '#666',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ marginTop: 0, color: '#333' }}>Welcome to Paper Management Dashboard</h3>
          <p>Please select a conference from the dropdown above to view papers with assigned reviewers and status.</p>
        </div>
      )}
    </div>
  );
};

export default PaperManagement;
